import * as THREE from 'three';
import type { TierDef } from './tiers';

/**
 * Procedural stylized geometries — strong silhouettes, not plain spheres.
 * All shapes normalized roughly to unit radius then scaled by caller.
 */

function seedNoise(i: number, j: number): number {
  const n = Math.sin(i * 12.9898 + j * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

/** Soft organic blob via deformed icosahedron */
export function createBlobGeometry(detail = 3, deform = 0.12): THREE.BufferGeometry {
  const geo = new THREE.IcosahedronGeometry(1, detail);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const n1 = seedNoise(v.x * 3.1, v.y * 2.7);
    const n2 = seedNoise(v.y * 4.2, v.z * 3.3);
    const n3 = seedNoise(v.z * 2.8, v.x * 3.9);
    const d = 1 + (n1 + n2 + n3 - 1.5) * deform;
    // Slight vertical squash for character
    v.multiplyScalar(d);
    v.y *= 0.92 + n2 * 0.06;
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  geo.computeVertexNormals();
  return geo;
}

/** Pointed sparkle / crystal candy */
export function createSparkGeometry(): THREE.BufferGeometry {
  const group: THREE.BufferGeometry[] = [];
  // Core
  const core = new THREE.OctahedronGeometry(0.72, 0);
  group.push(core);
  // Spikes
  const spike = new THREE.ConeGeometry(0.18, 0.55, 5);
  const mats = [
    [0, 1, 0],
    [0, -1, 0],
    [1, 0, 0],
    [-1, 0, 0],
    [0, 0, 1],
    [0, 0, -1],
  ];
  const geoms: THREE.BufferGeometry[] = [core];
  for (const [x, y, z] of mats) {
    const s = spike.clone();
    s.rotateX(y !== 0 ? 0 : Math.PI / 2);
    if (y < 0) s.rotateZ(Math.PI);
    if (x !== 0) {
      s.rotateZ(x > 0 ? -Math.PI / 2 : Math.PI / 2);
    }
    if (z !== 0) {
      s.rotateX(z > 0 ? Math.PI / 2 : -Math.PI / 2);
    }
    s.translate(x * 0.55, y * 0.55, z * 0.55);
    geoms.push(s);
  }
  return mergeGeometries(geoms);
}

/** Fluffy cloud from overlapping spheres */
export function createCloudGeometry(): THREE.BufferGeometry {
  const spheres: THREE.BufferGeometry[] = [];
  const parts = [
    [0, 0, 0, 0.7],
    [0.45, 0.1, 0.1, 0.5],
    [-0.48, 0.05, -0.05, 0.52],
    [0.15, 0.35, -0.2, 0.42],
    [-0.2, 0.28, 0.25, 0.4],
    [0.05, -0.3, 0.1, 0.38],
  ];
  for (const [x, y, z, r] of parts) {
    const g = new THREE.SphereGeometry(r as number, 16, 12);
    g.translate(x as number, y as number, z as number);
    spheres.push(g);
  }
  return mergeGeometries(spheres);
}

/** Smooth shiny orb with slight drip bottom */
export function createOrbGeometry(): THREE.BufferGeometry {
  const geo = new THREE.SphereGeometry(1, 32, 24);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    // Subtle teardrop
    if (v.y < 0) {
      v.y *= 1.08;
      const t = -v.y;
      v.x *= 1 - t * 0.08;
      v.z *= 1 - t * 0.08;
    }
    // Tiny top swirl bump
    if (v.y > 0.7) {
      v.y += 0.06 * (v.y - 0.7);
    }
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  geo.computeVertexNormals();
  return geo;
}

/** Crescent cookie moon */
export function createCrescentGeometry(): THREE.BufferGeometry {
  const outer = new THREE.SphereGeometry(1, 28, 20);
  // Carve crescent via vertex push
  const pos = outer.attributes.position as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    // Flatten slightly (cookie)
    v.z *= 0.55;
    // Crescent indent on +X
    const indent = Math.max(0, v.x - 0.15);
    const pull = indent * indent * 0.85;
    v.x -= pull * 0.9;
    // Craters (noise dents)
    const n = seedNoise(v.x * 5, v.y * 5);
    if (n > 0.72 && v.z > 0) {
      v.z -= (n - 0.72) * 0.35;
    }
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  outer.computeVertexNormals();
  return outer;
}

/** Macaron disc sandwich */
export function createDiscGeometry(): THREE.BufferGeometry {
  const top = new THREE.SphereGeometry(0.95, 24, 16, 0, Math.PI * 2, 0, Math.PI * 0.5);
  top.scale(1, 0.55, 1);
  top.translate(0, 0.28, 0);
  const bot = new THREE.SphereGeometry(0.95, 24, 16, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5);
  bot.scale(1, 0.55, 1);
  bot.translate(0, -0.28, 0);
  const cream = new THREE.CylinderGeometry(0.78, 0.78, 0.28, 24);
  cream.translate(0, 0, 0);
  // Ruffle rim on cream
  const ruffle = new THREE.TorusGeometry(0.78, 0.08, 8, 24);
  ruffle.rotateX(Math.PI / 2);
  return mergeGeometries([top, bot, cream, ruffle]);
}

/** Pudding planet with ring */
export function createRingGeometry(): THREE.BufferGeometry {
  const body = createBlobGeometry(2, 0.08);
  body.scale(0.92, 0.88, 0.92);
  const ring = new THREE.TorusGeometry(1.15, 0.1, 10, 40);
  ring.rotateX(Math.PI / 2.6);
  ring.rotateY(0.2);
  // Top cream swirl
  const swirl = new THREE.TorusGeometry(0.35, 0.12, 8, 16);
  swirl.rotateX(Math.PI / 2);
  swirl.translate(0, 0.75, 0);
  return mergeGeometries([body, ring, swirl]);
}

/** 5-point star cake */
export function createStarGeometry(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  const points = 5;
  const outer = 1;
  const inner = 0.45;
  for (let i = 0; i < points * 2; i++) {
    const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? outer : inner;
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  const extrude = new THREE.ExtrudeGeometry(shape, {
    depth: 0.55,
    bevelEnabled: true,
    bevelThickness: 0.08,
    bevelSize: 0.06,
    bevelSegments: 2,
  });
  extrude.center();
  extrude.rotateX(-Math.PI / 2);
  // Slight puff
  const pos = extrude.attributes.position as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    v.y += 0.08 * Math.sin(v.x * 3) * Math.cos(v.z * 3);
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  extrude.computeVertexNormals();
  return extrude;
}

/** Soft-serve swirl tower */
export function createSwirlGeometry(): THREE.BufferGeometry {
  const geoms: THREE.BufferGeometry[] = [];
  const layers = 7;
  for (let i = 0; i < layers; i++) {
    const t = i / (layers - 1);
    const y = -0.55 + t * 1.25;
    const r = 0.75 * (1 - t * 0.55) + 0.12;
    const g = new THREE.TorusGeometry(r * 0.55, r * 0.42, 10, 20);
    g.rotateX(Math.PI / 2);
    g.rotateY(t * Math.PI * 1.8);
    g.translate(Math.sin(t * 4) * 0.06, y, Math.cos(t * 4) * 0.06);
    geoms.push(g);
  }
  // Cherry on top
  const cherry = new THREE.SphereGeometry(0.22, 12, 10);
  cherry.translate(0, 0.85, 0);
  geoms.push(cherry);
  const stem = new THREE.CylinderGeometry(0.03, 0.03, 0.2, 6);
  stem.translate(0.05, 1.0, 0);
  stem.rotateZ(-0.4);
  geoms.push(stem);
  return mergeGeometries(geoms);
}

/** Faceted gem truffle */
export function createGemGeometry(): THREE.BufferGeometry {
  const geo = new THREE.DodecahedronGeometry(1, 0);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    // Soften slightly
    v.multiplyScalar(0.95 + seedNoise(v.x, v.y) * 0.08);
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  geo.computeVertexNormals();
  // Inner glow core suggestion via smaller offset shell not needed — material handles it
  return geo;
}

/** Multi-tier cake planet */
export function createCakeGeometry(): THREE.BufferGeometry {
  const tiers = [
    { y: -0.45, r: 1.0, h: 0.38 },
    { y: -0.05, r: 0.78, h: 0.34 },
    { y: 0.32, r: 0.55, h: 0.3 },
  ];
  const geoms: THREE.BufferGeometry[] = [];
  for (const t of tiers) {
    const g = new THREE.CylinderGeometry(t.r, t.r * 1.02, t.h, 28);
    g.translate(0, t.y, 0);
    geoms.push(g);
    // Frosting lip
    const lip = new THREE.TorusGeometry(t.r * 0.98, 0.06, 8, 28);
    lip.rotateX(Math.PI / 2);
    lip.translate(0, t.y + t.h * 0.48, 0);
    geoms.push(lip);
  }
  // Top ornament
  const star = new THREE.OctahedronGeometry(0.28, 0);
  star.translate(0, 0.72, 0);
  geoms.push(star);
  // Candles
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const c = new THREE.CylinderGeometry(0.04, 0.04, 0.22, 6);
    c.translate(Math.cos(a) * 0.32, 0.58, Math.sin(a) * 0.32);
    geoms.push(c);
    const flame = new THREE.SphereGeometry(0.06, 8, 6);
    flame.translate(Math.cos(a) * 0.32, 0.72, Math.sin(a) * 0.32);
    geoms.push(flame);
  }
  return mergeGeometries(geoms);
}

function mergeGeometries(geoms: THREE.BufferGeometry[]): THREE.BufferGeometry {
  // Manual merge without BufferGeometryUtils dependency issues
  const meshes = geoms.map((g) => {
    const m = new THREE.Mesh(g);
    return m;
  });
  // Use three's merge if available via manual position concat
  let totalVerts = 0;
  let totalIdx = 0;
  for (const g of geoms) {
    totalVerts += g.attributes.position!.count;
    const idx = g.index;
    totalIdx += idx ? idx.count : g.attributes.position!.count;
  }
  const positions = new Float32Array(totalVerts * 3);
  const normals = new Float32Array(totalVerts * 3);
  const indices: number[] = [];
  let vOffset = 0;
  let iOffset = 0;

  const p = new THREE.Vector3();
  const n = new THREE.Vector3();
  const normalMatrix = new THREE.Matrix3();

  for (let gi = 0; gi < geoms.length; gi++) {
    const g = geoms[gi]!;
    const mesh = meshes[gi]!;
    mesh.updateMatrixWorld(true);
    const mat = mesh.matrix;
    normalMatrix.getNormalMatrix(mat);
    const posAttr = g.attributes.position as THREE.BufferAttribute;
    const normAttr = g.attributes.normal as THREE.BufferAttribute | undefined;
    for (let i = 0; i < posAttr.count; i++) {
      p.fromBufferAttribute(posAttr, i).applyMatrix4(mat);
      positions[(vOffset + i) * 3] = p.x;
      positions[(vOffset + i) * 3 + 1] = p.y;
      positions[(vOffset + i) * 3 + 2] = p.z;
      if (normAttr) {
        n.fromBufferAttribute(normAttr, i).applyMatrix3(normalMatrix).normalize();
      } else {
        n.set(0, 1, 0);
      }
      normals[(vOffset + i) * 3] = n.x;
      normals[(vOffset + i) * 3 + 1] = n.y;
      normals[(vOffset + i) * 3 + 2] = n.z;
    }
    if (g.index) {
      for (let i = 0; i < g.index.count; i++) {
        indices.push(g.index.getX(i) + iOffset);
      }
    } else {
      for (let i = 0; i < posAttr.count; i++) {
        indices.push(iOffset + i);
      }
    }
    vOffset += posAttr.count;
    iOffset += posAttr.count;
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  merged.setIndex(indices);
  // Normalize to fit roughly in unit sphere
  merged.computeBoundingSphere();
  const bs = merged.boundingSphere!;
  const s = 1 / Math.max(bs.radius, 0.001);
  merged.translate(-bs.center.x, -bs.center.y, -bs.center.z);
  merged.scale(s, s, s);
  merged.computeVertexNormals();
  // Dispose sources
  for (const g of geoms) g.dispose();
  return merged;
}

const geometryCache = new Map<string, THREE.BufferGeometry>();

export function getGeometryForTier(tier: TierDef): THREE.BufferGeometry {
  const key = tier.shape;
  let cached = geometryCache.get(key);
  if (cached) return cached;

  let geo: THREE.BufferGeometry;
  switch (tier.shape) {
    case 'spark':
      geo = createSparkGeometry();
      break;
    case 'blob':
      geo = createBlobGeometry(3, 0.14);
      break;
    case 'cloud':
      geo = createCloudGeometry();
      break;
    case 'orb':
      geo = createOrbGeometry();
      break;
    case 'crescent':
      geo = createCrescentGeometry();
      break;
    case 'disc':
      geo = createDiscGeometry();
      break;
    case 'ring':
      geo = createRingGeometry();
      break;
    case 'star':
      geo = createStarGeometry();
      break;
    case 'swirl':
      geo = createSwirlGeometry();
      break;
    case 'gem':
      geo = createGemGeometry();
      break;
    case 'cake':
      geo = createCakeGeometry();
      break;
    default:
      geo = createBlobGeometry();
  }
  geometryCache.set(key, geo);
  return geo;
}

export function disposeGeometryCache(): void {
  for (const g of geometryCache.values()) g.dispose();
  geometryCache.clear();
}
